import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { deductCallCreditAction } from '../../../../../shared/actions/creditAction';
import {
  buildDeductCallCreditPayload,
  MIN_BILLED_CALL_DURATION_SEC,
  resolvePostCallBillingDurationSec,
} from '../utils/callFormatUtils';
import {
  getCallMediaTypeFromVoiceUi,
  ROOM_SCOPED_CALL_KEY_PREFIX,
  UNKNOWN_ROOM_ID_SEGMENT,
} from '../constant';

/**
 * Subscriber: POST wallet/deduct-call-credit once per call when the call ends and billed duration is long enough.
 */
export function useSubscriberCallCreditDeduction({
  isCreator,
  lastCallEndedReason,
  lastCallHangupParty,
  durationAccumulatedSecRef,
  durationStartAtRef,
  latestDisplayedCallDurationSecRef,
  pauseDurationTimer,
  creatorInfo,
  resolvedUserId,
  isVoiceCallUi,
  activeCall,
  roomId,
  subscriberWalletCredits,
}) {
  const dispatch = useDispatch();
  const lastKnownCallIdRef = useRef(null);
  const deductSentForCallIdRef = useRef(null);

  useEffect(() => {
    if (activeCall?.callId) lastKnownCallIdRef.current = activeCall.callId;
  }, [activeCall?.callId]);

  useEffect(() => {
    deductSentForCallIdRef.current = null;
    lastKnownCallIdRef.current = null;
  }, [roomId]);

  useEffect(() => {
    if (isCreator) return;
    if (!lastCallEndedReason) return;

    // Freeze duration before any early return (e.g. room info still loading). Otherwise
    // CallingScreen's reset effect can wipe refs while we wait, and deduct never sees duration.
    pauseDurationTimer();

    const creatorId = resolvedUserId || creatorInfo?._id || creatorInfo?.id || null;
    if (!creatorId || !creatorInfo) return;

    // activeCall can already be null by the time end reason arrives.
    // Fall back to a room-scoped key so we still deduct once per ended call.
    const callKey =
      activeCall?.callId ??
      lastKnownCallIdRef.current ??
      `${ROOM_SCOPED_CALL_KEY_PREFIX}${roomId || UNKNOWN_ROOM_ID_SEGMENT}`;
    if (deductSentForCallIdRef.current === callKey) return;

    const callKind = getCallMediaTypeFromVoiceUi(isVoiceCallUi);
    const durationSec = resolvePostCallBillingDurationSec(
      durationAccumulatedSecRef,
      durationStartAtRef,
      latestDisplayedCallDurationSecRef,
      { creatorInfo, callKind, endedReason: lastCallEndedReason }
    );
    if (durationSec < MIN_BILLED_CALL_DURATION_SEC) return;

    const payload = buildDeductCallCreditPayload(creatorId, creatorInfo, callKind, durationSec, {
      hangupParty: lastCallHangupParty,
      endedReason: lastCallEndedReason,
      subscriberWalletCredits,
    });
    if (!payload) return;

    deductSentForCallIdRef.current = callKey;

    void dispatch(deductCallCreditAction(payload));
  }, [
    isCreator,
    lastCallEndedReason,
    lastCallHangupParty,
    resolvedUserId,
    creatorInfo,
    isVoiceCallUi,
    activeCall?.callId,
    durationAccumulatedSecRef,
    durationStartAtRef,
    latestDisplayedCallDurationSecRef,
    pauseDurationTimer,
    roomId,
    subscriberWalletCredits,
    dispatch,
  ]);
}
